import React, { useEffect } from 'react';
import { View } from '@tarojs/components';
import * as Taro from '@tarojs/taro';
import GameBoard from '@/components/GameBoard';
import GameCard from '@/components/GameCard';
import CardSlot from '@/components/CardSlot';
import { GameState, getReserveCards } from '@/utils/gameEngine';
import { reserveStacksOf } from '@/utils/staging';
import { CARD_SIZE } from '@/data/board';
import { Card } from '@/types/game';
import { Flight } from './useCardMotion';
import styles from './index.module.scss';

const Playfield: React.FC<{
  state: GameState; onClick: (id: string) => void; flights: Flight[];
  queue: Card[]; hiddenSlotIds: string[]; hiddenTransitionIds: string[]; eliminatingIds: string[]; effect: string; onLayout: () => void;
}> = ({ state, onClick, flights, queue, hiddenSlotIds, hiddenTransitionIds, eliminatingIds, effect, onLayout }) => {
  const reserve = getReserveCards(state);
  const byId = new Map(reserve.map(card => [card.id, card]));
  const stacks = reserveStacksOf(state.reserveCardIds, state.reserveGroups, state.reserveColumns);
  const reserveKey = state.reserveCardIds.join(',');
  useEffect(() => {
    const timer = setTimeout(onLayout, 50);
    return () => clearTimeout(timer);
  }, [reserveKey, onLayout]);
  const width = typeof Taro.getWindowInfo === 'function' ? Taro.getWindowInfo().windowWidth : 375;
  const basePixels = CARD_SIZE * width / 750;
  return (
    <View className={styles.playfield} id='game-playfield'>
      <View className={styles.boardArea}>
        <GameBoard cards={state.cards} onCardClick={onClick} hiddenCardIds={hiddenTransitionIds} onLayout={onLayout} effect={effect} />
      </View>
      <View className={styles.staging} data-staging='true'>
        <View className={styles.reserveCards}>
          {stacks.map((stack, column) => <View key={column} className={styles.reserveStack} data-reserve-column={column}>
            {stack.map((id, index) => {
              const front = index === stack.length - 1;
              return <View key={id} className={styles.reserveTile + (!front ? ' ' + styles.buried : '')}
                data-reserve-front={front ? 'true' : 'false'}
                style={{ transform: 'translateY(-' + Math.min(stack.length - index - 1, 3) * 4 + 'rpx)', zIndex: index + 1 }}
              >
                <GameCard card={byId.get(id)!} small onClick={onClick} motionDisabled={!front} hidden={hiddenTransitionIds.includes(id)} />
              </View>;
            })}
          </View>)}
        </View>
      </View>
      <CardSlot queue={queue} eliminatingIds={eliminatingIds} hiddenIds={hiddenSlotIds} />
      {flights.map(flight => (
        <View key={flight.key} className={styles.flight} data-flight='true' style={{
          left: (flight.from.left - flight.root.left) + 'px', top: (flight.from.top - flight.root.top) + 'px',
          transitionDuration: flight.duration + 'ms',
          transform: 'translate3d(' + (flight.travelling ? flight.to.left - flight.from.left : 0) + 'px,' +
            (flight.travelling ? flight.to.top - flight.from.top : 0) + 'px,0) scale(' +
            ((flight.travelling ? flight.to.width : flight.from.width) / basePixels) + ')'
        }}
        ><GameCard card={{ ...flight.card, size: CARD_SIZE }} x={0} y={0} /></View>
      ))}
    </View>
  );
};
export default Playfield;
